import React, { ReactNode } from 'react';

/**
 * @interface PrivilegedWrapperProps
 * @property {string[]} allow       - List of roles that should be granted access
 * @property {ReactNode} children   - The content to render if access is granted
 * @property {string[]} roles       - The roles assigned to the current user
 */
interface PrivilegedWrapperProps {
    allow: string[];
    children: ReactNode;
    roles: string[];
}

/**
 * Checks for user privileges and renders the child components conditionally
 *
 * If none of the user's roles are found in the `allow` list, the wrapper
 * will render nothing.
 *
 * @param {string[]} allow        - List of roles that should be granted access
 * @param {ReactNode} children    - The content to render if access is granted
 * @param {string[]} roles        - The roles assigned to the current user
 * @returns {JSX.Element}         - The children if allowed, otherwise an empty fragment
 */
export const PrivilegedWrapper: React.FC<PrivilegedWrapperProps> = ({ allow, children, roles }) => {
    const hasRoles = Array.isArray(roles) && roles.length > 0;
    const hasAllow = Array.isArray(allow) && allow.length > 0;

    // // Roles are expected from upstream auth state. Wait until available.
    if (!hasRoles || !hasAllow) {
        return null;
    }

    const isPrivileged = allow.some((allowedRole: string) => roles.includes(allowedRole));

    if (!isPrivileged) {
        return <></>;
    }

    return <>{children}</>;
};

export default PrivilegedWrapper;
